import { useNavigate } from 'react-router-dom'

import { Button, IllustrationType, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

export interface AddEditSubprojectModalProps {
  onSubmit: ({ name }: AddEditSubprojectFormValues) => Promise<UnhandledResult<Result<unknown>>>
  loading: boolean
  title: string
  description: React.ReactNode
  iconName: IllustrationType
  nameLabel?: string
  submitButton: string
  subprojectName?: string
}

export interface AddEditSubprojectFormValues {
  name: string
}

const AddEditSubprojectModal = ({
  onSubmit,
  loading,
  title,
  description,
  iconName,
  nameLabel,
  submitButton,
  subprojectName,
}: AddEditSubprojectModalProps) => {
  const navigate = useNavigate()

  const onClose = () => {
    navigate(-1)
  }

  const form = useForm<AddEditSubprojectFormValues>({
    defaultValues: {
      name: subprojectName || '',
    },
    validate: (data) => {
      const errors: FormErrors<AddEditSubprojectFormValues> = {}

      if (!data.name) {
        errors.name = i18n.t('project_management.modals.error_messages.mandatory')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await onSubmit({
        name: data.name,
      })

      const handled = handleResult(result, (errors) => {
        if (errors.name)
          errors.name = i18n.t('project_management.modals.error_messages.subproject_name_exists')

        setErrors(errors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        onClose()
      }

      return handled
    },
  })

  return (
    <Modal
      illustration={iconName}
      title={title}
      description={<Text size="300">{description}</Text>}
      onClose={onClose}
      onSubmit={() => {
        form.dirty ? form.handleSubmit() : onClose()
      }}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={loading} submit>
            {submitButton}
          </Button>
        </>
      }
    >
      <TextField
        label={nameLabel}
        value={form.values.name}
        onChange={(value) => form.setValues({ ...form.values, name: value })}
        error={form.touched.name ? form.errors.name : undefined}
        mandatory
      />
    </Modal>
  )
}

export default observer(AddEditSubprojectModal)
